import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Mail, MessageSquare, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import PageMeta from "../../components/seo/PageMeta";
import { SHIFTS_CTAS } from "../../data/marketplaceData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShiftsContact() {
  const [form, setForm] = useState({ name: "", email: "", role: "clinician", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}`;
      const r = await fetch(`/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify({
          source: "shiftbridge-contact",
          name: form.name,
          email: form.email,
          subject: "TRITAL ShiftBridge™ support",
          message: `Role: ${form.role}\n\n${form.message || ""}`,
          idempotencyKey: requestId,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.ok === false) throw new Error("Submission failed");
      toast.success("Message received.", { description: "We'll respond within one business hour." });
      setDone(true);
    } catch {
      toast.error("We couldn't send your message", { description: "Please try again, or email info@tritalcare.com." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ShiftsLayout>
      <PageMeta
        title="Contact & support — TRITAL ShiftBridge™ by TRITAL Care®"
        description="Reach the TRITAL ShiftBridge™ support desk. Real humans, 1 business hour SLA. Email, phone, and SMS."
        canonical="https://www.tritalcare.com/shiftbridge/contact"
      />

      <section className="relative overflow-hidden bg-trital-mesh">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-[900px] mx-auto px-5 lg:px-8 pt-12 pb-10 lg:pt-20 lg:pb-14">
          <div className="overline text-trital-red">Support</div>
          <h1 className="mt-3 font-display font-black text-5xl lg:text-[60px] tracking-tight leading-[1.02] text-slate-900 text-balance">
            One message. One business hour. Real humans.
          </h1>
          <p className="mt-4 text-slate-600 max-w-[58ch]">Pick the fastest channel for what you need — or use the form below if you'd prefer email.</p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-5 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
        {/* Channels */}
        <div className="space-y-3">
          {[
            { Icon: Phone, label: "Call recruiter desk", val: "+1 (832) 303-6622", href: "tel:+18323036622" },
            { Icon: MessageSquare, label: "Text recruiter line", val: "(832) 344-1525", href: "sms:+18323441525" },
            { Icon: Mail, label: "Email support", val: "support@tritalcare.com", href: "mailto:support@tritalcare.com" },
            { Icon: Mail, label: "Compliance docs", val: "compliance@tritalcare.com", href: "mailto:compliance@tritalcare.com" },
          ].map((c) => (
            <a key={c.label} href={c.href} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-card hover:border-trital-navy transition-colors" data-testid={`contact-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center"><c.Icon className="w-5 h-5" strokeWidth={1.8} /></div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">{c.label}</div>
                <div className="mt-1 font-display font-bold text-trital-navy text-[16px]">{c.val}</div>
              </div>
            </a>
          ))}
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 text-[13px] text-slate-600 inline-flex items-start gap-3">
            <Clock className="w-4 h-4 text-trital-red mt-0.5 shrink-0" strokeWidth={1.8} />
            <div>
              <div className="font-display font-bold text-slate-900 text-[14px] mb-1">Hours</div>
              Recruiter desk staffed 7 days a week, 6 AM–10 PM CT. Critical-shift escalations after hours: text the line above.
            </div>
          </div>
        </div>

        {/* Form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-3xl bg-white border border-slate-200 shadow-navy p-7 lg:p-8"
          data-testid="contact-form"
        >
          {done ? (
            <div className="text-center py-10">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 text-2xl">✓</div>
              <div className="mt-5 font-display font-black text-2xl text-slate-900">Message received.</div>
              <p className="mt-2 text-[14px] text-slate-600">We'll respond within one business hour.</p>
              <Link to={SHIFTS_CTAS.browse} className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep">
                Browse shifts <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-display font-black text-2xl text-slate-900">Send us a message</h2>
              <p className="mt-1.5 text-[13px] text-slate-600">We'll route it to the right desk and reply within 1 business hour.</p>
              <div className="mt-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-[12px] font-semibold text-slate-700 mb-1.5 inline-block">Full name *</span>
                    <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="contact-name" />
                  </label>
                  <label className="block">
                    <span className="text-[12px] font-semibold text-slate-700 mb-1.5 inline-block">Email *</span>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="contact-email" />
                  </label>
                </div>
                <label className="block">
                  <span className="text-[12px] font-semibold text-slate-700 mb-1.5 inline-block">I'm a…</span>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="contact-role">
                    <option value="clinician">Clinician</option>
                    <option value="facility">Facility / scheduler</option>
                    <option value="recruiter">Recruiter / partner</option>
                    <option value="press">Press / media</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-slate-700 mb-1.5 inline-block">Message *</span>
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} data-testid="contact-message" placeholder="What can we help with?" />
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-navy text-white text-[14px] font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {submitting ? "Sending…" : <>Send message <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></>}
                </button>
                <div className="text-[11px] text-slate-500 text-center">By sending, you agree to our <Link to="/legal/privacy" className="hover:underline">privacy policy</Link>.</div>
              </div>
            </>
          )}
        </motion.form>
      </section>
    </ShiftsLayout>
  );
}

const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-[14px] focus:outline-none focus:border-trital-navy focus:ring-2 focus:ring-trital-navy/10 transition";
